import { useCallback, useEffect, useState } from 'react'
import { isRole, roleHasCapability } from '@qim/shared'
import { ApiError, fetchCustomerUsers, inviteCustomerUser, removeCustomerUser } from '../api.js'
import { formatInstant } from '../format.js'
import type { CustomerUsers } from '../api.js'
import type { FormEvent, JSX } from 'react'

/**
 * A customer's own users (IM-153).
 *
 * Who on the customer's side may use the product, set by a `customer-admin`
 * for their own people or by Qrvey for the first admin. Two things are said
 * rather than left to be inferred:
 *
 * - **Qrvey staff appear read-only.** Their access is Qrvey's to manage, and
 *   the customer's control over it is in their own AWS account — so the list
 *   shows who is working on the account without offering a control that would
 *   misrepresent where the boundary is.
 * - **Whether an invitation went out.** Customer sign-in is not live in every
 *   environment yet; a user recorded without an email is said to be exactly
 *   that, rather than implying they have been told.
 *
 * Refusals come back from the API with a sentence, and the sentence is shown:
 * "that person already has access to another customer" is an instruction, and
 * "forbidden" is not.
 */

const canManage = (role: string): boolean => isRole(role) && roleHasCapability(role, 'users:manage')

const ROLE_WORDS: Readonly<Record<string, string>> = {
  'customer-admin': 'Admin — deploys, and manages users',
  'customer-viewer': 'Viewer — sees status and cost',
}

const refusalOf = (error: unknown): string =>
  error instanceof ApiError &&
  typeof (error.body as { detail?: unknown } | undefined)?.detail === 'string'
    ? String((error.body as { detail: string }).detail)
    : error instanceof ApiError && error.status === 403
      ? 'Your role does not permit that (users:manage).'
      : 'Could not save that just now.'

export const CustomerUsersPanel = ({
  baseUrl,
  tenantId,
  role,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
}): JSX.Element | null => {
  const [data, setData] = useState<CustomerUsers | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  /*
   * A failed *read* is said quietly, apart from a refused *action*. This panel
   * sits on the customer screen beside its own alerts, and a background read
   * that could not answer is not news worth interrupting a screen reader for.
   */
  const [readFailure, setReadFailure] = useState<string | undefined>(undefined)
  const [note, setNote] = useState<string | undefined>(undefined)
  const [email, setEmail] = useState('')
  const [newRole, setNewRole] = useState<'customer-admin' | 'customer-viewer'>('customer-viewer')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await fetchCustomerUsers(baseUrl, tenantId))
      setReadFailure(undefined)
    } catch {
      setReadFailure('Could not read this customer’s users just now.')
    }
  }, [baseUrl, tenantId])

  useEffect(() => {
    if (canManage(role)) void load()
  }, [load, role])

  if (!canManage(role)) return null

  const invite = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    setFailure(undefined)
    setNote(undefined)
    try {
      const result = await inviteCustomerUser(baseUrl, tenantId, email.trim(), newRole)
      setNote(
        result.invitation === 'sent'
          ? `Invited ${email.trim()}. Cognito has emailed them a temporary password.`
          : result.invitation === 'already-has-sign-in'
            ? `${email.trim()} already had a sign-in, so no new email was sent. Their access is updated.`
            : `${email.trim()} is recorded, but no invitation was sent: customer sign-in is not live in this environment yet.`,
      )
      setEmail('')
      await load()
    } catch (error: unknown) {
      setFailure(refusalOf(error))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (subject: string): Promise<void> => {
    setFailure(undefined)
    setNote(undefined)
    try {
      await removeCustomerUser(baseUrl, tenantId, subject)
      await load()
    } catch (error: unknown) {
      setFailure(refusalOf(error))
    }
  }

  const active = (data?.users ?? []).filter((u) => u.revokedAt === undefined)
  const removed = (data?.users ?? []).filter((u) => u.revokedAt !== undefined)

  return (
    <section className="panel">
      <h2>Users</h2>
      <p className="hint">The customer&rsquo;s own people. Each belongs to this customer only.</p>

      {readFailure === undefined ? null : <p className="failure">{readFailure}</p>}
      {failure === undefined ? null : (
        <p role="alert" className="failure">
          {failure}
        </p>
      )}
      {note === undefined ? null : <p role="status">{note}</p>}

      {data === undefined ? null : active.length === 0 ? (
        <p className="muted">No users yet.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Role</th>
                <th scope="col">Last changed</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {active.map((user) => (
                <tr key={user.email}>
                  <td>{user.email}</td>
                  <td>{ROLE_WORDS[user.role] ?? user.role}</td>
                  <td>
                    {formatInstant(user.updatedAt)}
                    <p className="muted">by {user.updatedBy}</p>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => void remove(user.email)}
                      aria-label={`Remove ${user.email}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {removed.length === 0 ? null : (
        <p className="muted">
          Removed:{' '}
          {removed.map((u) => `${u.email} (${formatInstant(u.revokedAt ?? '')})`).join(', ')}
        </p>
      )}

      <form onSubmit={(event) => void invite(event)}>
        <div className="field-row">
          <div className="field">
            <label htmlFor="inviteEmail">Email</label>
            <input
              id="inviteEmail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="inviteRole">Role</label>
            <select
              id="inviteRole"
              value={newRole}
              onChange={(event) =>
                setNewRole(event.target.value as 'customer-admin' | 'customer-viewer')
              }
            >
              <option value="customer-viewer">Viewer</option>
              <option value="customer-admin">Admin</option>
            </select>
          </div>
        </div>
        <p className="hint">
          A viewer sees status, deployments and cost. An admin also deploys, destroys, and manages
          users.
        </p>
        <button type="submit" className="primary" disabled={busy || email.trim() === ''}>
          {busy ? 'Saving…' : 'Invite user'}
        </button>
      </form>

      {data === undefined ? null : (
        <div className="staff-on-tenant">
          <h3>Qrvey staff</h3>
          <p className="hint">
            Managed by Qrvey, not from here. What Qrvey can do in your cloud account is set by the
            stacks you deployed there.
          </p>
          {data.staff.length === 0 ? null : (
            <ul>
              {data.staff.map((s) => (
                <li key={s.email}>
                  {s.email} <span className="muted">({s.role})</span>
                </li>
              ))}
            </ul>
          )}
          {data.staffWithAllCustomers === 0 ? null : (
            <p className="muted">
              Plus {data.staffWithAllCustomers} Qrvey administrator
              {data.staffWithAllCustomers === 1 ? '' : 's'} with access to every customer.
            </p>
          )}
        </div>
      )}
    </section>
  )
}
